import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * The five standard dialog sizes (roadmap §4.2) — every Modal call site must
 * pick one instead of declaring an ad hoc width. `fullscreen` fills the
 * viewport on every breakpoint (wizards, complex multi-step flows); the rest
 * fall back to fullscreen on mobile and cap at their max width from `sm:` up.
 * `sm`/`md` (confirm dialogs, single-section forms) size to their content
 * instead of stretching to the `lg`/`xl` scrollable-body height.
 */
const sizeClassName = {
  sm: "sm:w-[420px] sm:h-auto sm:max-h-[90vh]",
  md: "sm:w-[560px] sm:h-auto sm:max-h-[90vh]",
  lg: "sm:w-[760px] sm:h-[90vh] sm:max-h-[720px]",
  xl: "sm:w-[960px] sm:h-[90vh] sm:max-h-[720px]",
  fullscreen: "sm:w-screen sm:h-[100dvh] sm:rounded-none sm:border-0",
} as const;

export type ModalSize = keyof typeof sizeClassName;

/**
 * A centered, large floating dialog (desktop). Built on Radix Dialog (focus
 * trap, escape, a11y, backdrop) like SideSheet/BottomSheet, but centered in
 * the viewport with a fixed-ish width/height instead of anchored to an edge.
 * Header stays pinned; the body is left to the consumer to scroll (so a
 * sticky footer — e.g. form actions — can sit below the scroll area).
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  icon,
  closeLabel = "Close",
  size = "lg",
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** A line under the title. */
  description?: string;
  /** A tinted icon beside the title; also gives the close button a bordered frame. */
  icon?: ReactNode;
  closeLabel?: string;
  /** One of the five standard sizes (§4.2). Defaults to `lg` (760px). */
  size?: ModalSize;
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
          <Dialog.Content
            className={cn(
              "flex h-[100dvh] w-screen flex-col rounded-none border-0 border-border bg-card text-card-foreground shadow-lg",
              // Full-bleed on a phone, so the chrome has to clear the notch, and
              // the body has to end above the keyboard rather than behind it —
              // both reset once the dialog floats from `sm:` up.
              "pt-[var(--safe-top)] pb-[var(--keyboard-inset,0px)]",
              "sm:rounded-lg sm:border sm:pt-0 sm:pb-0",
              "modal-content-motion",
              sizeClassName[size],
              className,
            )}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-6 py-5">
              <div className="flex min-w-0 items-center gap-3">
                {icon !== undefined ? (
                  <span
                    aria-hidden="true"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
                  >
                    {icon}
                  </span>
                ) : null}
                <div className="min-w-0">
                  <Dialog.Title className="text-h3">{title}</Dialog.Title>
                  {description !== undefined ? (
                    <Dialog.Description className="mt-0.5 text-sm text-muted-foreground">
                      {description}
                    </Dialog.Description>
                  ) : null}
                </div>
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className={cn(
                    "rounded-md p-1 text-muted-foreground hover:bg-muted",
                    icon !== undefined &&
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border p-0",
                  )}
                  aria-label={closeLabel}
                >
                  ✕
                </button>
              </Dialog.Close>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">{children}</div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
